import { useRef } from "react";
import type { Paywall } from "@/react/types";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { usePunditUserContext } from "@circle-react/contexts";
import { FullScreenModal } from "@circle-react-shared/ModalsV2/FullScreenModal";
import { Form } from "@circle-react-uikit/Form";
import { Loader } from "@circle-react-uikit/Loader";
import { tabs } from "../constants";
import { PaywallFormContentOrchestrator } from "./PaywallFormContentOrchestrator";
import { PaywallFormProviderContext } from "./PaywallFormProvider";
import { usePaywallAdminFormModal } from "./hooks/usePaywallAdminFormModal";

export interface PaywallsAdminFormModalComponentProps {
  initialTab?: (typeof tabs)[keyof typeof tabs];
  paywall?: Paywall;
  refetchPaywalls: () => void;
}
const PaywallsAdminFormModalComponent = ({
  paywall,
  refetchPaywalls,
  initialTab,
}: PaywallsAdminFormModalComponentProps) => {
  const modal = useModal();

  const {
    currentCommunitySettings: communitySettings,
    isLoading: isLoadingCommunitySettings,
  } = usePunditUserContext();

  const validToClose = useRef(() => true);
  const onClose = (withConfirmation = false) => {
    if (!withConfirmation || validToClose.current()) {
      void modal.hide();
      modal.remove();
    }
  };

  const {
    getDefaultValues,
    setActiveTab,
    activeTab,
    handleSubmit,
    mutation,
    currentPaywallStatus,
  } = usePaywallAdminFormModal({
    initialTab,
    paywall,
    refetchPaywalls,
    onClose,
    isModalVisible: modal.visible,
  });

  if (isLoadingCommunitySettings) {
    return <Loader />;
  }

  const isCurrencyRequired =
    paywall?.policies?.requires_currency ??
    Boolean(communitySettings?.currencies_enabled);

  const canChangeCurrency = paywall?.policies?.can_change_currency ?? true;

  const isWorkflowsActiveTab = activeTab === tabs.WORKFLOWS_TAB;
  const hasSubscribers = Boolean(
    paywall?.id &&
      (paywall?.total_active_onetime_subscribers != "0" ||
        paywall?.total_active_recurring_subscribers != "0"),
  );

  const submitHandler = isWorkflowsActiveTab ? () => {} : handleSubmit;

  return (
    <FullScreenModal
      isOpen={modal.visible}
      contentPortalId="paywall-admin-modal"
      onClose={() => onClose(true)}
      size="screen"
    >
      <Form
        onSubmit={submitHandler}
        data-testid="paywall-admin-modal"
        defaultValues={getDefaultValues(paywall)}
        className="h-max-screen-dynamic lg:h-full"
        formClassName="h-max-screen-dynamic h-full overflow-hidden"
      >
        {({ formState: { isSubmitting } }) => (
          <PaywallFormProviderContext
            onClose={onClose}
            validToClose={validToClose}
          >
            <PaywallFormContentOrchestrator
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              isSubmitting={isSubmitting}
              mutation={mutation}
              currentPaywallStatus={currentPaywallStatus}
              requiresCurrency={isCurrencyRequired}
              canChangeCurrency={canChangeCurrency}
              hasSubscribers={hasSubscribers}
            />
          </PaywallFormProviderContext>
        )}
      </Form>
    </FullScreenModal>
  );
};

const PaywallsAdminFormModal = ModalManager.create(
  (prop: PaywallsAdminFormModalComponentProps) => (
    <PaywallsAdminFormModalComponent {...prop} />
  ),
);

export const usePaywallsAdminFormModal = () => useModal(PaywallsAdminFormModal);
